import { ContractApiResponse, ParticipantResponse } from '../../types/api-responses';
import { ContractGuaranteeItem, ContractAsReceivableItem } from '../../types/operations';
import { loadBusinessPartnerFromParticipants } from '../helpers/operationHelpers';
import { BusinessPartnerData } from '../../components/form/BusinessPartnerManager';
import { formatLocalDateToBR } from '../../../../../utils/DateUtils';

// ============================================
// METADATA HELPERS
// ============================================

export interface ContractMetadata {
    apiVersion?: string;
    amendmentReason?: string;
    amendmentNumber?: number;
    billingStatus: { billed?: boolean; billing_info?: string };
}

export const extractMetadata = (data: ContractApiResponse): ContractMetadata => ({
    apiVersion: data.apiName && data.apiVersion ? `${data.apiName} - ${data.apiVersion}` : undefined,
    amendmentReason: data.amendment_reason || undefined,
    amendmentNumber: data.amendment_number || undefined,
    billingStatus: { billed: data.billed, billing_info: data.billing_info || undefined },
});

// ============================================
// PARTICIPANTS HELPERS
// ============================================

export interface ParticipantInfo {
    uuid: string;
    name: string;
    document: string;
    id: number | null;
    person_id: string;
}

const EMPTY_UUID = '00000000-0000-0000-0000-000000000000';

const mapParticipantToInfo = (participant: ParticipantResponse): ParticipantInfo => ({
    uuid: participant.person_id || EMPTY_UUID,
    name: participant.name || '',
    document: participant.document || '',
    id: participant.id ? Number(participant.id) : null,
    person_id: participant.person_id || EMPTY_UUID,
});

export const extractAssignor = (data: ContractApiResponse): ParticipantInfo | null => {
    const assignor = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'ASSIGNOR');
    return assignor ? mapParticipantToInfo(assignor) : null;
};

export const extractRecipient = (data: ContractApiResponse): ParticipantInfo | null => {
    const recipient = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'RECIPIENT');
    return recipient ? mapParticipantToInfo(recipient) : null;
};

export const extractBusinessPartner = (data: ContractApiResponse): BusinessPartnerData | null =>
    loadBusinessPartnerFromParticipants(data.participants);

// ============================================
// FILES HELPERS
// ============================================

export interface ExistingFiles {
    contractFile: string;
    artifactFiles: string[];
}

export const extractFilesFromSchema = (schema: any): ExistingFiles | null => {
    if (!schema.files) return null;
    return {
        contractFile: schema.files.contract_file || '',
        artifactFiles: schema.files.artefacts || [],
    };
};

// ============================================
// CONTRACT GUARANTEE HELPERS
// ============================================

export interface ContractGuaranteeState {
    items: ContractGuaranteeItem[];
    files: Map<string, File>;
    existingFiles: Map<string, string>;
}

/**
 * Extracts contract guarantee data from schema supporting multiple formats:
 * 1. collaterals.assets.contracts.items (new format)
 * 2. schema.contracts.items (cessão root format)
 * 3. schema.contract_guarantee (legacy single object)
 */
export const extractContractGuaranteeFromSchema = (schema: any): ContractGuaranteeState | null => {
    const items = resolveContractGuaranteeItems(schema);
    if (!items?.length) return null;

    const existingFiles = new Map<string, string>();
    items.forEach((item: any) => {
        if (item.file_name) {
            existingFiles.set(item.collateral_id || item.file_name, item.file_name);
        }
    });

    return {
        items: items.map((item: any) => ({
            collateral_id: item.collateral_id || crypto.randomUUID(),
            contract_number: item.contract_number || '',
            contract_value: item.contract_value || 0,
            anticipated_value: item.anticipated_value || 0,
            file_name: item.file_name || '',
            description: item.description || '',
            drawee_person_id: item.drawee_person_id || undefined,
            drawee_document_type: item.drawee_document_type || undefined,
            drawee_document: item.drawee_document || '',
            drawee_name: item.drawee_name || '',
            drawee_city: item.drawee_city || '',
            drawee_state: item.drawee_state || '',
            document_title: item.document_title || '',
            title_installment: item.title_installment || '',
            issue_date: item.issue_date ? formatLocalDateToBR(item.issue_date) : '',
            operation_date: item.operation_date ? formatLocalDateToBR(item.operation_date) : '',
            present_value: item.present_value || 0,
            due_date: item.due_date ? formatLocalDateToBR(item.due_date) : '',
            maturity_value: item.maturity_value || 0,
        })),
        files: new Map(),
        existingFiles,
    };
};

const resolveContractGuaranteeItems = (schema: any): any[] | null => {
    if (schema.collaterals?.assets?.contracts?.items?.length) {
        return schema.collaterals.assets.contracts.items;
    }
    if (schema.contracts?.items?.length) {
        return schema.contracts.items;
    }
    if (schema.contract_guarantee) {
        return Array.isArray(schema.contract_guarantee)
            ? schema.contract_guarantee
            : [schema.contract_guarantee];
    }
    return null;
};

// ============================================
// CONTRACTS AS RECEIVABLES HELPERS
// ============================================

export interface ContractsAsReceivablesState {
    items: ContractAsReceivableItem[];
    files: Map<string, File>;
    existingFiles: Map<string, string>;
}

/**
 * Resolve os itens de contrato-como-recebível do schema.
 * Formato novo: operation_dictionary.contracts_as_receivables.
 * Compat: operation_dictionary.contracts (formato antigo da Cessão, quando
 * "Contrato cedido" era gravado como `contracts` no nível raiz).
 */
const resolveContractsAsReceivablesItems = (schema: any): any[] | null => {
    if (schema.contracts_as_receivables?.items?.length) {
        return schema.contracts_as_receivables.items;
    }
    if (schema.contracts?.items?.length) {
        return schema.contracts.items;
    }
    return null;
};

/**
 * Extrai os contratos dados como recebíveis do schema para o estado do form.
 * Mapeia collateral_id (formato antigo) para receivable_contract_id.
 */
export const extractContractsAsReceivablesFromSchema = (schema: any): ContractsAsReceivablesState | null => {
    const items = resolveContractsAsReceivablesItems(schema);
    if (!items?.length) return null;

    const existingFiles = new Map<string, string>();
    const mappedItems: ContractAsReceivableItem[] = items.map((item: any) => {
        const id = item.receivable_contract_id || item.collateral_id || crypto.randomUUID();
        if (item.file_name) {
            existingFiles.set(id, item.file_name);
        }
        return {
            receivable_contract_id: id,
            contract_number: item.contract_number || '',
            contract_value: item.contract_value || 0,
            contract_issue_date: item.contract_issue_date ? formatLocalDateToBR(item.contract_issue_date) : '',
            file_name: item.file_name || '',
            description: item.description || '',
            drawee_person_id: item.drawee_person_id || undefined,
            drawee_document_type: item.drawee_document_type || undefined,
            drawee_document: item.drawee_document || '',
            drawee_name: item.drawee_name || '',
            drawee_address_street: item.drawee_address_street || '',
            drawee_address_number: item.drawee_address_number || '',
            drawee_address_complement: item.drawee_address_complement || '',
            drawee_address_district: item.drawee_address_district || '',
            drawee_city: item.drawee_city || '',
            drawee_state: item.drawee_state || '',
            drawee_address_zip_code: item.drawee_address_zip_code || '',
            drawee_address_ibge_code: item.drawee_address_ibge_code || '',
            contract_installment: item.contract_installment ?? item.title_installment ?? '',
            operation_date: item.operation_date ? formatLocalDateToBR(item.operation_date) : '',
            present_value: item.present_value || 0,
            due_date: item.due_date ? formatLocalDateToBR(item.due_date) : '',
            installment_maturity_value: item.installment_maturity_value ?? item.receivable_amount ?? item.maturity_value ?? 0,
        };
    });

    return {
        items: mappedItems,
        files: new Map(),
        existingFiles,
    };
};
